import { Trash } from '@phosphor-icons/react'
import { Button, Form } from 'antd'
import { type NamePath } from 'antd/es/form/interface'
import { forwardRef, ReactNode, Ref, useImperativeHandle, useRef } from 'react'

export interface FormDynamicFieldGroupMethods {
  add: (position?: number) => void
  remove: (index: number) => void
}

interface FormDynamicFieldGroupProps<T = any> {
  fieldName: NamePath
  initialItem: T
  renderItem: (props: {
    field: { name: number; fieldKey?: number }
    index: number
    actions: {
      remove: () => void
      duplicate: () => void
    }
  }) => ReactNode
}

export const FormDynamicFieldGroup = forwardRef<
  FormDynamicFieldGroupMethods,
  FormDynamicFieldGroupProps<any>
>(
  <T extends object>(
    { fieldName, initialItem, renderItem }: FormDynamicFieldGroupProps<T>,
    ref: Ref<unknown> | undefined
  ) => {
    const form = Form.useFormInstance()

    const methodsRef = useRef<{
      add?: (defaultValue?: T, insertIndex?: number) => void
      remove?: (index: number) => void
    }>({})

    useImperativeHandle(ref, () => ({
      add: (position?: number) => {
        if (methodsRef.current.add) {
          methodsRef.current.add(initialItem, position)
        }
      },
      remove: (index: number) => {
        if (methodsRef.current.remove) {
          methodsRef.current.remove(index)
        }
      },
      duplicate: (index: number) => {
        const currentValues = form.getFieldValue(fieldName) as T[]
        if (currentValues?.[index]) {
          form.setFieldsValue({
            [fieldName]: [...currentValues, currentValues[index]],
          })
        }
      },
    }))

    const handleDuplicate = (name: number, currentValues: T[]) => {
      form.setFieldsValue({
        [fieldName]: [...currentValues, currentValues[name]],
      })
    }

    return (
      <Form.List name={fieldName}>
        {(fields, { add, remove }) => {
          methodsRef.current = {
            add: (item, insertIndex) => add(item, insertIndex),
            remove: (index) => remove(index),
          }
          return fields.map(({ key, name, ...restField }, index) => {
            const currentValues = form.getFieldValue(fieldName) as T[]
            const disabledDelete = fields.length === 1

            const actions = {
              remove: () => remove(name),
              duplicate: () => handleDuplicate(name, currentValues),
            }

            return (
              <div className="mb-4 flex items-end gap-4" key={key}>
                {renderItem({
                  field: { name, ...restField },
                  index,
                  actions,
                })}
                <Button
                  danger
                  className="mb-2 h-[38px]"
                  disabled={disabledDelete}
                  icon={<Trash />}
                  onClick={actions.remove}
                  type="dashed"
                />
              </div>
            )
          })
        }}
      </Form.List>
    )
  }
)
